import { notifySignedOut } from '../auth/sessionSignal'
import { wsUrl } from '../transport/origin'
import type { CommandState } from './serverList'

/**
 * One socket to the cloud, carrying command state as it changes.
 *
 * This layer used to ask "is it done yet" on a timer. That timer was inherited from a
 * heartbeat below it — the answer took ~30s anyway, so a few seconds looked free — and
 * once delivery became a push the timer WAS the latency. Asking repeatedly is not a
 * cheap version of being told; it is the same design one layer up.
 *
 * The socket is shared by every waiter. Cookies ride the handshake, so nothing here
 * holds a credential.
 */
type Waiter = (state: CommandState) => void

const waiters = new Map<string, Set<Waiter>>()
const deviceListeners = new Set<(devices: unknown[]) => void>()
const accountRevListeners = new Set<(rev: string) => void>()
const browserTabsListeners = new Set<() => void>()
const heldListeners = new Set<(held: string[]) => void>()
const ptyListeners = new Set<(frames: PtyFrame[]) => void>()
const errorListeners = new Set<(code: string, frame: Record<string, unknown>) => void>()
const chatBusyListeners = new Set<(convId: string) => void>()
const agentShareListeners = new Set<(shareId: string, what: string) => void>()
/** Frames queued while the socket is not yet open, replayed in order once it is. */
const pending: string[] = []
let socket: WebSocket | null = null
/*
 * PARKED FOR THE BACK/FORWARD CACHE — owner, 2026-08-08: 每次進入 app 都會
 * refresh 一次，很麻煩.
 *
 * WebKit refuses to put a page with an OPEN WebSocket into the bfcache, so
 * holding this socket across a backgrounding turned every return to the app
 * into a full reload — the "refresh" was the browser reloading a page it was
 * forbidden to freeze. On pagehide the socket is closed DELIBERATELY (parking
 * suppresses the reconnect backoff a close would otherwise schedule); on
 * pageshow it reopens at once. A restored page reconnects in one round trip —
 * strictly cheaper than the reload it replaces, and the stream was never a
 * source of truth anyway: every consumer reconciles from the store on open.
 */
let parking = false
/** The socket in ANY state, including CONNECTING — `socket` is only assigned on
 *  open, and a half-open WebSocket blocks the bfcache exactly like an open one. */
let liveWs: WebSocket | null = null
let opening = false
/** Reconnect backoff, reset by a successful open. */
let retryMs = 500
/** How long a handshake may go unanswered before it is treated as dead. */
const CONNECT_TIMEOUT_MS = 8_000

/**
 * Whether the stream is currently carrying updates.
 *
 * Callers use this to decide how hard to fall back, so it must mean "OPEN right now",
 * not "we intend to have one". A hopeful answer here turns the safety net off exactly
 * when it is needed.
 */
/**
 * Open the socket now, before anything needs it.
 *
 * Called once at start-up. Connecting lazily made the FIRST command of a session pay
 * for the handshake — the one command where the user is watching a sheet they just
 * opened. A socket that is already up turns that cost into zero, and an idle one costs
 * nothing while nothing happens.
 */
export function connectStream(): void {
  ensureSocket()
}

/**
 * Devices as the cloud has them, pushed on change.
 *
 * The list is also fetched over HTTP for the first frame. A socket that never opens
 * must not mean an empty screen, and an empty list must not be able to mean "we never
 * heard" — so this only ever UPDATES a list that HTTP already drew.
 */
export function onDevices(cb: (devices: unknown[]) => void): () => void {
  deviceListeners.add(cb)
  return () => deviceListeners.delete(cb)
}

/**
 * The account's revision, as each view frame carries it (`account_rev`).
 *
 * It moves when something the frame does NOT carry changed for this account — a pin,
 * the friend graph — and for nothing else. Opaque: compare for equality only (it also
 * differs after the cloud restarts). A cloud that predates it sends none, and then
 * this never fires. servers/accountRev.ts turns it into "re-read now".
 */
export function onAccountRev(cb: (rev: string) => void): () => void {
  accountRevListeners.add(cb)
  return () => accountRevListeners.delete(cb)
}

/**
 * The tabs agents hold in this account's browsers changed (`{browser_tabs: true}` —
 * the BFF's routes_browser, at most once a second per account). A frame of its own,
 * NOT the account bell: titles change by the second, and the bell makes every screen
 * re-read the machines. Only an open list listens (More › Chrome tabs).
 */
export function onBrowserTabs(cb: () => void): () => void {
  browserTabsListeners.add(cb)
  return () => browserTabsListeners.delete(cb)
}

/**
 * The receipt for hold/release: every agent this connection currently holds.
 *
 * Worth reading rather than assuming, because a hold that did not take and one that
 * did look identical from here — and the difference is whether the next message lands
 * in the middle of a sentence someone is typing.
 */
/** Errors the cloud reports about a frame this client sent. */
export function onStreamError(
  cb: (code: string, frame: Record<string, unknown>) => void,
): () => void {
  errorListeners.add(cb)
  return () => errorListeners.delete(cb)
}

/**
 * One terminal frame, coming up.
 *
 * `data` is TEXT — the control messages this machine's own `/ws` already speaks, so
 * the terminal code above reads them exactly as it always did. `b64` is terminal
 * BYTES: a pty emits bytes in chunks that fall wherever the kernel put them, and
 * decoding each chunk as text turns a character split across two reads into two
 * replacement characters, permanently. That damage looks like a broken font rather
 * than like a transport bug, which is why the bytes travel encoded instead.
 */
export type PtyFrame = {
  /**
   * `act` is a quiet stream's heartbeat-shaped NON-heartbeat: the edge sends it
   * in place of terminal bytes the opener asked not to receive (Clean View's
   * headless socket), at most one per 250ms and only while the pane is actually
   * writing. It exists so "the agent is working" still reaches the busy dot and
   * the transcript re-read without the 2717 B/s of repaints it used to ride on.
   */
  op: 'out' | 'closed' | 'act'
  stream: string
  data?: string
  b64?: string
  reason?: string
  /**
   * The machine said no, rather than the connection dropping.
   *
   * They look identical from a closed socket and want opposite responses: a drop
   * should reconnect, a refusal must not — retrying something that will keep
   * answering the same sentence is a loop with an explanation inside it that
   * nobody reads.
   */
  refused?: boolean
}

/** Be told what a terminal says. Returns an unsubscribe. */
export function onPty(cb: (frames: PtyFrame[]) => void): () => void {
  ptyListeners.add(cb)
  ensureSocket()
  return () => ptyListeners.delete(cb)
}

const streamOpenListeners = new Set<() => void>()

/**
 * Be told each time the account stream (re)opens — page restored from the
 * bfcache, network back, a half-open zombie killed by the liveness probe.
 *
 * The stream is not a source of truth: anything pushed while it was parked or
 * dead is GONE, and this signal is the exact moment that absence becomes
 * knowable. So a consumer that renders pushed state must re-read its store
 * here. The device/command console gets that read for free — the cloud sends
 * its whole state on connect — which is why, until the DM view subscribed, it
 * was the one consumer whose missed frames only ever surfaced on a manual
 * refresh: the suspended-phone report.
 */
export function onStreamOpen(cb: () => void): () => void {
  streamOpenListeners.add(cb)
  return () => streamOpenListeners.delete(cb)
}

export function onHeld(cb: (held: string[]) => void): () => void {
  heldListeners.add(cb)
  return () => heldListeners.delete(cb)
}

/** Send a frame, queueing it if the socket is still coming up. */
export function sendFrame(frame: Record<string, unknown>): void {
  const raw = JSON.stringify(frame)
  if (socket?.readyState === WebSocket.OPEN) socket.send(raw)
  else {
    pending.push(raw)
    ensureSocket()
  }
}

export function streamOpen(): boolean {
  return socket?.readyState === WebSocket.OPEN
}

/**
 * Resolve once the events socket is OPEN — or after `timeoutMs`, with the truth.
 *
 * For callers about to ask a question whose ANSWER can only arrive on this
 * socket (the relay: submit by POST, answer by push). On a fresh load the first
 * status queries fire while this socket is still connecting; the machine answers
 * promptly, the frame lands on a socket that is not there, and nothing stores it
 * — so the waiter times out into "the machine did not answer" and a refresh
 * "fixes" it. Asking while deaf is the bug; this is the one-line cure at the
 * only seam every relayed request passes through.
 *
 * Bounded, never a gate: on timeout the caller proceeds exactly as before and
 * earns the same honest error — a permanently-down socket must not convert
 * every request into an infinite hang.
 */
export function whenStreamOpen(timeoutMs = 2_000): Promise<boolean> {
  if (streamOpen()) return Promise.resolve(true)
  ensureSocket()
  return new Promise((resolve) => {
    const off = onStreamOpen(() => {
      clearTimeout(timer)
      off()
      resolve(true)
    })
    const timer = setTimeout(() => {
      off()
      resolve(streamOpen())
    }, timeoutMs)
  })
}

/*
 * THE READYSTATE LIES, and this is the recovery for when it does.
 *
 * A phone that blinks networks or a Safari tab that was suspended keeps a half-open
 * TCP socket whose readyState reads OPEN for minutes while nothing can arrive. The
 * owner measured the consequence: a create that finished in under a second on the
 * machine sat on "Starting…" indefinitely, and every push-refreshed panel froze with
 * it — the app's whole no-heartbeat design assumes the stream SPEAKS when there is
 * news, and a half-open socket is a stream that has stopped speaking while still
 * claiming to listen.
 *
 * So: on the two moments that suggest the world changed underneath us — the tab
 * returning to the foreground, the network coming back — send one frame the cloud
 * must answer (an unknown type draws an error receipt by contract; no screen shows
 * it, onError has no UI consumers) and give the answer a deadline. Any frame at all
 * cancels the verdict; silence closes the socket, which drives the existing
 * onclose → reconnect path.
 *
 * EVENT-DRIVEN, NEVER PERIODIC. This fires on user-shaped transitions only — it is
 * the same rule as refetchOnWindowFocus, applied to the transport itself. A periodic
 * ping would be the heartbeat this codebase bans, and it would also have hidden this
 * bug instead of fixing it.
 */
const PROBE_TIMEOUT_MS = 4_000
let probeVerdict: ReturnType<typeof setTimeout> | null = null

/*
 * Told when an in-flight probe reaches its verdict, either way: a frame arrived
 * (the socket is alive), or silence closed it (the corpse is dead and the
 * reconnect is scheduled). `whenProbeSettles` below is the only consumer.
 */
const probeSettleWaiters = new Set<() => void>()

function settleProbe(): void {
  for (const cb of [...probeSettleWaiters]) cb()
  probeSettleWaiters.clear()
}

export function probeLiveness(): void {
  const ws = socket
  if (!ws || ws.readyState !== WebSocket.OPEN) {
    ensureSocket()
    return
  }
  if (probeVerdict !== null) return // one probe in flight is enough
  probeVerdict = setTimeout(() => {
    probeVerdict = null
    // Silence past the deadline: the socket is a corpse wearing OPEN. Close it
    // ourselves — onclose schedules the reconnect and resets the backoff path.
    ws.close()
    settleProbe()
  }, PROBE_TIMEOUT_MS)
  try {
    ws.send(JSON.stringify({ type: 'liveness-probe' }))
  } catch {
    clearTimeout(probeVerdict)
    probeVerdict = null
    ws.close()
    settleProbe()
  }
}

/**
 * Probe the stream and resolve once the verdict is in — with the pipe usable
 * again if it can be.
 *
 * For a caller whose EVIDENCE of trouble is its own starvation: a relayed read
 * that timed out while `streamOpen()` said true. The probes above fire on
 * user-shaped transitions (foreground, online) — but the owner hit the case
 * those never cover (2026-08-25): staring at "Waiting for tbc-m1 to answer" on
 * a phone whose socket half-died UNDER the open app. No visibility change, no
 * online event, server reap an hour away — nothing ever probed, so every retry
 * rode the same corpse and starved the same way, and only a reload (a brand-new
 * socket) recovered. The Caddy log for that window showed every ask accepted
 * (202, sub-second) and every edge answering: the loss was this screen's own
 * receive path, exactly what the probe exists to detect.
 *
 * So the starving caller brings the trigger: fire one probe, wait for its
 * verdict, and — when the verdict killed the corpse — wait for the reconnect
 * the close scheduled, bounded. Not a heartbeat: it runs only on the failure
 * evidence, like the visibility probe runs only on the return-to-foreground
 * evidence.
 */
export function whenProbeSettles(timeoutMs = PROBE_TIMEOUT_MS + 2_000): Promise<void> {
  probeLiveness()
  if (probeVerdict === null) {
    // No probe armed: the socket was not OPEN (a reconnect is already underway)
    // or the send failed and closed it just now. Either way the useful wait is
    // for the stream to be back, bounded the same way.
    return whenStreamOpen(timeoutMs).then(() => undefined)
  }
  return new Promise<void>((resolve) => {
    let done = false
    const finish = (): void => {
      if (done) return
      done = true
      clearTimeout(safety)
      probeSettleWaiters.delete(finish)
      // Verdict "alive" leaves the socket open and this resolves at once;
      // verdict "corpse" closed it, so give the scheduled reconnect a bounded
      // chance to land before the caller resubmits.
      if (streamOpen()) resolve()
      else void whenStreamOpen(timeoutMs).then(() => resolve())
    }
    probeSettleWaiters.add(finish)
    // The verdict timer above settles us; this is the belt for a probe whose
    // socket vanished between arming and answering.
    const safety = setTimeout(finish, timeoutMs)
  })
}

/**
 * When the cloud last said anything on this socket — any frame at all. 0 until the
 * first one. This is the only evidence of life that costs nothing: a socket that
 * spoke a moment ago is alive; one that has been silent for a while is unknown.
 */
let lastInboundAt = 0

/**
 * How long the socket may have been silent before a SEND stops trusting it.
 *
 * Under this, a frame proved the pipe within the probe's own deadline — a probe
 * would learn nothing. Over it, the probe costs one round trip on a live pipe and
 * finds the corpse on a dead one. Kept short on purpose: this is paid once per
 * quiet spell, by a person pressing send, never by a timer.
 */
const TRUST_SILENCE_MS = 10_000

/**
 * The stream, TRUSTED — open, and either recently heard from or probed just now.
 *
 * The starving read got its probe on 2026-08-25 (`whenProbeSettles`), and the
 * owner's second report (2026-09-05: 有時候從 Text editor 想要 cmd+enter 輸入 … 會
 * 有卡住無法輸出的問題, refresh 網頁之後就好了) is the same corpse on the WRITE side,
 * which had none. A relayed mutation cannot be retried — it may have run — so its
 * only safe moment for the probe is BEFORE it submits; and a terminal keystroke
 * written into a half-open socket is simply gone, with `readyState` still saying
 * OPEN and nothing on screen to say otherwise. Both used to wait on the corpse
 * until a reload built a new socket, which is exactly the report.
 *
 * So the send is the event: if the socket has been silent longer than
 * TRUST_SILENCE_MS, fire the probe and wait for its verdict — alive, and we
 * proceed at the cost of one round trip; dead, and the close drives the reconnect
 * the caller then waits for, bounded. Still no heartbeat: nothing here fires
 * without a person pressing send.
 */
export function whenStreamTrusted(timeoutMs = PROBE_TIMEOUT_MS + 2_000): Promise<void> {
  if (!streamOpen()) return whenStreamOpen(timeoutMs).then(() => undefined)
  if (Date.now() - lastInboundAt < TRUST_SILENCE_MS) return Promise.resolve()
  return whenProbeSettles(timeoutMs)
}

// Wired once at module load; guarded so the test environment without a document
// keeps importing this file cleanly.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') probeLiveness()
  })
}
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => probeLiveness())
  // The bfcache contract, both halves. pagehide fires for BOTH backgrounding and
  // real navigation away; closing is correct for either. pageshow with
  // e.persisted means the page came back FROM the cache — the win this exists
  // for — but reopening unconditionally is right anyway: a fresh load's socket
  // is opened by Authed, and ensureSocket is idempotent on an open one.
  window.addEventListener('pagehide', () => {
    parking = true
    try {
      ;(liveWs ?? socket)?.close()
    } catch {
      /* already closing */
    }
    liveWs = null
    socket = null
  })
  window.addEventListener('pageshow', () => {
    if (!parking) return
    parking = false
    ensureSocket()
  })
}

const edgeEventListeners = new Set<(event: string, data: Record<string, unknown>) => void>()

/*
 * Transport-death subscribers — owner, 2026-08-08: 有時候一個 session 放太久，
 * 會卡住。也無法輸入。需要退出再進來才能變化.
 *
 * Everything multiplexed over this socket (the relay ptys above all) dies WITH
 * it on the far side: the BFF releases every pty stream when the socket closes,
 * and tells the EDGE — the browser hears nothing. RelaySocket therefore stayed
 * readyState OPEN forever over a stream the cloud had already buried: keystrokes
 * went into a stream id nobody knew, the reconnect machinery saw "ready" and
 * stood down, and only a remount (exit and re-enter) ever recovered. The wire
 * has no death notice, so this is it: every close of the underlying socket —
 * parking, network loss, the liveness probe's kill — is announced here, and the
 * facades synthesize their own close, which is what wakes the reconnect code
 * that already knew what to do.
 */
const transportDownListeners = new Set<() => void>()

/** Be told when the underlying account socket closes, for any reason. */
export function onTransportDown(cb: () => void): () => void {
  transportDownListeners.add(cb)
  return () => transportDownListeners.delete(cb)
}

/** Edge events relayed through /account/events. Returns an unsubscribe. */
export function onEdgeEvent(fn: (event: string, data: Record<string, unknown>) => void): () => void {
  edgeEventListeners.add(fn)
  return () => edgeEventListeners.delete(fn)
}

/** Conversation-busy pokes, fanned by the BFF when an agent's busy bit flips on
 *  its machine's link (owner, 2026-08-27: 朋友視角…忙碌頭像顯示的不及時). The frame
 *  names only WHICH conversation moved; the busy truth is re-asked through
 *  conv_busy, so the halo stays single-source. */
export function onChatBusy(fn: (convId: string) => void): () => void {
  chatBusyListeners.add(fn)
  return () => chatBusyListeners.delete(fn)
}

/** A shared agent moved (owner, 2026-10-17: share an agent with a friend) — offered, changed,
 *  accepted, declined, revoked, or the agent started or finished a turn ("activity"). Told to
 *  both sides by the BFF (routes_agent_share.py). Carries no state: the screen asks again. */
export function onAgentShare(fn: (shareId: string, what: string) => void): () => void {
  agentShareListeners.add(fn)
  return () => agentShareListeners.delete(fn)
}

function dispatch(raw: string): void {
  lastInboundAt = Date.now()
  // ANY frame is proof of life — including an error receipt about the probe itself.
  if (probeVerdict !== null) {
    clearTimeout(probeVerdict)
    probeVerdict = null
    settleProbe()
  }
  let msg: { command_id?: unknown; state?: unknown; result?: unknown; error?: unknown }
  try {
    msg = JSON.parse(raw) as typeof msg
  } catch {
    // Unreadable, not merely unfamiliar — say so rather than treating it as silence.
    for (const cb of [...errorListeners]) cb('unreadable_frame', { raw: raw.slice(0, 200) })
    return
  }
  if (Array.isArray((msg as { devices?: unknown }).devices)) {
    const devices = (msg as { devices: unknown[] }).devices
    for (const cb of [...deviceListeners]) cb(devices)
  }
  const rev = (msg as { account_rev?: unknown }).account_rev
  if (typeof rev === 'string' && rev) {
    for (const cb of [...accountRevListeners]) cb(rev)
  }
  /*
   * Errors the cloud sends back about a frame WE sent — a misspelled type, or a hold
   * for an agent this account never attached. Both used to be silent: the frame was
   * ignored, or the hold "succeeded" for a hash nobody owns. From this side either one
   * looked exactly like working, which is the whole reason a receipt exists.
   */
  const browserTabs = (msg as { browser_tabs?: unknown }).browser_tabs === true
  if (browserTabs) {
    for (const cb of [...browserTabsListeners]) cb()
  }
  const err = typeof (msg as { error?: unknown }).error === 'string'
    ? String((msg as { error: string }).error)
    : null
  if (err) {
    for (const cb of [...errorListeners]) cb(err, msg as Record<string, unknown>)
  }
  const pty = (msg as { pty?: unknown }).pty
  const hasPty = Array.isArray(pty) && pty.length > 0
  if (hasPty) {
    for (const cb of [...ptyListeners]) cb(pty as PtyFrame[])
  }
  if (Array.isArray((msg as { held?: unknown }).held)) {
    const held = (msg as { held: unknown[] }).held.map(String)
    for (const cb of [...heldListeners]) cb(held)
  }
  /*
   * `commands` is ALWAYS an array — homepage confirmed every send site.
   *
   * This used to also accept a single command at the top level. Defensive parsing is
   * cheap, but a branch that can never run teaches the next reader that the singular
   * shape is legal, and then someone sends one. The tolerance was inventing a contract
   * neither side had agreed to.
   */
  const hasCommands = Array.isArray((msg as { commands?: unknown }).commands)
  const batch = hasCommands ? ((msg as { commands: Record<string, unknown>[] }).commands ?? []) : []
  for (const one of batch) dispatchCommand(one)

  /*
   * A frame matching nothing must be LOUD.
   *
   * Dropping it silently is the failure avengents-homepage and avengents-server both
   * hit from their own sides tonight: if the wire shape changes, every frame lands in
   * the "nothing to do" branch, the socket stays open, no error is raised, and the
   * screen simply stops updating. A shape mismatch is indistinguishable from a quiet
   * system — and quiet is what a working one looks like.
   *
   * Reported, not thrown: an unknown frame is not a reason to tear down a connection
   * that is otherwise carrying everything else correctly.
   */
  /*
   * Edge-originated events, fanned out by the BFF from the machine's link frame
   * (owner, 2026-08-08: homepage 部署之後把 event lane 接通). One shape:
   * {edge_event: {device_id, event, data}}. Routed to whoever subscribed —
   * project transcripts today, anything else tomorrow — through the same
   * listener discipline every other frame kind here uses.
   */
  const cbusy = (msg as { chat_busy?: { conv_id?: unknown } }).chat_busy
  if (cbusy && typeof cbusy.conv_id === 'string') {
    for (const fn of [...chatBusyListeners]) fn(cbusy.conv_id)
  }
  const ashare = (msg as { agent_share?: { share_id?: unknown; what?: unknown } }).agent_share
  if (ashare && typeof ashare.share_id === 'string') {
    const what = typeof ashare.what === 'string' ? ashare.what : ''
    for (const fn of [...agentShareListeners]) fn(ashare.share_id, what)
  }
  const ev = (msg as {
    edge_event?: { event?: unknown; data?: unknown; project_id?: unknown; device_id?: unknown }
  }).edge_event
  if (ev && typeof ev.event === 'string') {
    const data = (ev.data ?? {}) as Record<string, unknown>
    // The wrapper's project_id is the addressed form (present-or-absent, never
    // null — the BFF's contract); fall back to the payload's own for an edge
    // that predates the lift. This lane is LATENCY, not delivery-of-record: the
    // BFF drops events under backpressure, so nothing here may be the only way
    // a fact reaches the screen — the store reconcile is the truth.
    if (typeof ev.project_id === 'string' && ev.project_id && !data['project_id']) {
      data['project_id'] = ev.project_id
    }
    // Same lift for the SENDING MACHINE: the BFF stamps it on the wrapper, and a
    // scoped consumer (the idle_changed re-ask) needs to know which machine to
    // re-ask without a second registry lookup.
    if (typeof ev.device_id === 'string' && ev.device_id && !data['device_id']) {
      data['device_id'] = ev.device_id
    }
    for (const cb of [...edgeEventListeners]) cb(ev.event, data)
  }

  const known =
    hasCommands ||
    hasPty ||
    Array.isArray((msg as { devices?: unknown }).devices) ||
    Array.isArray((msg as { held?: unknown }).held) ||
    (ev !== undefined && ev !== null) ||
    (cbusy !== undefined && cbusy !== null) ||
    (ashare !== undefined && ashare !== null) ||
    browserTabs ||
    err !== null
  if (!known) {
    for (const cb of [...errorListeners]) cb('unexpected_frame', msg as Record<string, unknown>)
  }
}

function dispatchCommand(msg: Record<string, unknown>): void {
  const id = typeof msg.command_id === 'string' ? msg.command_id : null
  const state = typeof msg.state === 'string' ? msg.state : null
  if (!id || !state) return
  const set = waiters.get(id)
  if (!set) return
  const payload = {
    state,
    result: (msg.result ?? null) as CommandState['result'],
    error: (msg.error ?? null) as CommandState['error'],
  } as CommandState
  for (const w of [...set]) w(payload)
}

function ensureSocket(): void {
  if (socket || opening) return
  opening = true
  let handshake: ReturnType<typeof setTimeout>
  let ws: WebSocket
  try {
    ws = new WebSocket(wsUrl('/account/events'))
  } catch {
    opening = false
    return // no stream; callers keep their fallback
  }
  liveWs = ws
  ws.onopen = () => {
    opening = false
    socket = ws
    retryMs = 500
    clearTimeout(handshake)
    // Anything asked for before the socket came up, in the order it was asked for.
    while (pending.length > 0) ws.send(pending.shift() as string)
    // The reconcile bell. Consumers re-read their store on open because frames
    // pushed while the pipe was down never arrive late — they never arrive.
    for (const cb of [...streamOpenListeners]) cb()
  }
  /*
   * A handshake that is never answered must not wedge this forever.
   *
   * A proxy that forwards HTTP and swallows the Upgrade accepts the TCP connection and
   * then says nothing at all — no open, no close, no error. Without this timer the
   * `opening` flag stays true for the life of the page, every later ensureSocket()
   * returns immediately, and the stream is permanently dead while every layer still
   * reports healthy. Both other agents hit this exact silence today from their own
   * sides; a hang leaves less evidence than a crash.
   *
   * One shot. It fires or it is cleared — nothing here repeats.
   */
  handshake = setTimeout(() => {
    if (socket !== ws) ws.close() // close() drives onclose, which schedules the retry
  }, CONNECT_TIMEOUT_MS)
  ws.onmessage = (e) => dispatch(String(e.data))
  ws.onclose = (e) => {
    clearTimeout(handshake)
    opening = false
    socket = null
    for (const cb of [...transportDownListeners]) cb()
    /*
     * A refusal does not become an acceptance by waiting.
     *
     * The cloud closes with 4001/4003 when this browser is no longer anyone. Retrying
     * that is a loop which never ends and never speaks: the backoff grows, the screen
     * says "not live", and the true answer — you are signed out — is never reached.
     * Say it instead, and stop. Everything else is worth another try.
     */
    if (e?.code === 4001 || e?.code === 4003) {
      notifySignedOut()
      return
    }
    if (parking) return // closed on purpose for the bfcache; pageshow reopens it
    // Reconnect regardless of whether anyone is waiting: the point of holding it open
    // is that the next command finds it already there.
    setTimeout(ensureSocket, retryMs)
    retryMs = Math.min(retryMs * 2, 10_000)
  }
  ws.onerror = () => ws.close()
}

/**
 * Be told when this command changes. Returns an unsubscribe.
 *
 * Subscribing does NOT relieve the caller of its own timeout: a socket that is open can
 * still miss a frame, and "the stream is up" must never become "the answer will come".
 */
export function onCommand(commandId: string, cb: Waiter): () => void {
  const set = waiters.get(commandId) ?? new Set<Waiter>()
  set.add(cb)
  waiters.set(commandId, set)
  ensureSocket()
  return () => {
    set.delete(cb)
    if (set.size === 0) waiters.delete(commandId)
    /*
     * The socket is NOT closed when the last waiter leaves. Closing it would put the
     * handshake back in front of the next command, which is the cost this removes; the
     * connection is what the page holds, not what a single command borrows.
     */
  }
}

/**
 * Hold an agent, and CHECK that the hold took.
 *
 * The connection is the lease: there is nothing to renew, and closing the tab releases
 * everything this socket held. What there is to get wrong is believing a hold applied
 * when it did not — the frame goes out, the receipt comes back without it, and nothing
 * complains. avengents-server hit the same shape from the other side today, recording a
 * refused batch as fully delivered; a losing outcome counted as a winning one is worse
 * than an error, because it is only visible to the person it happens to.
 *
 * So this resolves against the receipt: true when the agent is in `held`, false when a
 * receipt arrives without it, and false on a timeout — never optimistically.
 */
export function holdAgent(agent: string, timeoutMs = 3_000): Promise<boolean> {
  return new Promise((resolve) => {
    const done = (ok: boolean) => {
      off()
      offErr()
      clearTimeout(timer)
      resolve(ok)
    }
    const off = onHeld((held) => done(held.includes(agent)))
    /*
     * A named refusal beats waiting out the timeout: `unknown_agent` means this hold
     * will never take, and three seconds of hoping tells the caller nothing it does not
     * already know from the first frame back.
     */
    const offErr = onStreamError((code, frame) => {
      if (code === 'unknown_agent' && frame.agent === agent) done(false)
      if (code === 'unknown_frame') done(false)
    })
    const timer = setTimeout(() => {
      off()
      offErr()
      resolve(false) // no receipt is not a hold; the message would land mid-sentence
    }, timeoutMs)
    sendFrame({ type: 'hold', agent })
  })
}

/**
 * Release an agent. Deliberately not awaited on the way out: the socket closing does
 * the same job, so a page being torn down has nothing to wait for.
 */
export function releaseAgent(agent: string): void {
  sendFrame({ type: 'release', agent })
}

/**
 * The waiter registry, for a test that must prove its own hazard exists.
 *
 * A "first waiter" bug is only detectable while the command being addressed is not
 * first, and that ordering is an assumption until something reads it.
 */
export function waitersForTest(): ReadonlyMap<string, unknown> {
  return waiters
}

/**
 * Put this module back to the state it had at import — the seam shellTabs.ts and
 * drafts.ts already have, and the one this file needed.
 *
 * WHY IT EXISTS. `npm run test:shuffle` was red on 2 of 4 runs (measured 2026-09-01),
 * and one of the two causes was here: bfcachePark.test.ts does
 * `await import('./commandStream')`, which returns the module ALREADY CONNECTED by an
 * earlier suite. connectStream() then sees a live `socket`, opens nothing, and the
 * assertion reads `expected [] to have a length of 1`. Nothing was wrong with the code
 * under test; the test could not put it back to a known state.
 *
 * That was invisible because `verify` — what the pre-push hook runs — called plain
 * `vitest run`. test:shuffle existed in package.json and was in no gate, which by the
 * hook's own standard ("a check nobody depends on is theatre") made it theatre. It is
 * in `verify` now, so this seam is load-bearing rather than optional.
 *
 * It clears the SOCKETS and the BACKOFF but deliberately not the listener registries:
 * a subscriber that registered in module scope (the visibility and online handlers
 * installed at import) cannot re-register, so dropping them would leave the module
 * permanently deaf in a way no production path can produce. Tests that need a clean
 * registry unsubscribe with the disposer each `on*` already returns.
 */
export function __resetCommandStream(): void {
  try {
    liveWs?.close()
  } catch {
    /* a socket that will not close is already gone */
  }
  socket = null
  liveWs = null
  opening = false
  parking = false
  retryMs = 500
  pending.length = 0
  waiters.clear()
  if (probeVerdict) {
    clearTimeout(probeVerdict)
    probeVerdict = null
  }
}
